/* Furniture Instore — native Trustpilot product-review carousel for phones.

   Why this exists rather than another TrustBox: every TrustBox renders inside a
   cross-origin iframe on widget.trustpilot.com, so nothing on this page can
   restyle it or turn its cards into a carousel. The only lever a TrustBox gives
   is which template to ask for, and none of the product-review templates is a
   swipeable one-card-at-a-time layout.

   The data, though, is reachable. The endpoint below is the same one Trustpilot's
   own product-review widget calls, it needs no token, and it answers with
   Access-Control-Allow-Origin: * — verified against this business unit. So the
   reviews, their photos and the range's overall score can be fetched directly and
   drawn as ordinary markup that CSS can style.

   Deliberately additive. If the fetch fails, or the range turns out to have no
   reviews, the iframe widget is put back exactly as it was and the section's own
   no-reviews fallback is handed control — the customer never ends up with less
   than they had before.

   Everything it needs is already on the page: the SKU list, business unit id,
   locale and review languages all live as data attributes on the widget div that
   snippets/trustpilot-product-reviews.liquid renders. Reading them from there
   means the range-matching logic is never duplicated, and it keeps working when
   the variant picker rewrites data-sku. */
(function () {
  'use strict';

  var ENDPOINT = 'https://widget.trustpilot.com/trustbox-data/';
  var SVG_NS = 'http://www.w3.org/2000/svg';

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    /* textContent, never innerHTML — review bodies are customer-written. */
    if (text != null) node.textContent = text;
    return node;
  }

  /* Trustpilot's own star treatment: a filled box per whole star, a grey box for
     the rest. Half stars are not shown per-review because Trustpilot only ever
     gives whole numbers there; the summary average is printed as a figure. */
  function stars(count, size) {
    var wrap = el('span', 'fi-tpc__stars');
    wrap.setAttribute('aria-hidden', 'true');
    for (var i = 1; i <= 5; i++) {
      var box = el('span', 'fi-tpc__star' + (i <= Math.round(count) ? ' is-on' : ''));
      if (size) box.style.setProperty('--fi-tpc-star', size + 'px');
      var svg = document.createElementNS(SVG_NS, 'svg');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('focusable', 'false');
      var path = document.createElementNS(SVG_NS, 'path');
      path.setAttribute('d', 'M12 2.5l2.9 6.3 6.9.8-5.1 4.7 1.4 6.8L12 17.6 5.9 21.1l1.4-6.8L2.2 9.6l6.9-.8z');
      svg.appendChild(path);
      box.appendChild(svg);
      wrap.appendChild(box);
    }
    return wrap;
  }

  function formatDate(iso, locale) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    try {
      return d.toLocaleDateString(locale || 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch (e) {
      return d.toISOString().slice(0, 10);
    }
  }

  function formatCount(n, locale) {
    try {
      return Number(n).toLocaleString(locale || 'en-GB');
    } catch (e) {
      return String(n);
    }
  }

  /* Trustpilot returns each photo at several sizes. 360px wide is the one that
     fits a phone card without paying for the original, which can be several MB
     straight off a customer's camera. */
  function pickImage(attachment) {
    var files = (attachment && attachment.processedFiles) || [];
    var byName = {};
    files.forEach(function (f) {
      if (f && f.dimension && f.url) byName[f.dimension] = f.url;
    });
    return byName['360pxWide'] || byName['1024pxWide'] || byName.original || null;
  }

  /* Every photo across the whole range, in one strip above the carousel — the same
     shape Trustpilot's own desktop box uses, and the reason attachmentSummary is a
     separate list from the reviews rather than nested inside them.

     Each photo keeps its reviewId so tapping it can jump the carousel to the review
     it belongs to; without that the strip would be decoration the customer cannot
     act on. Deduped by attachment id because a review with several photos appears
     once per photo in the summary. */
  function galleryImages(data) {
    var summary = data.attachmentSummary || {};
    var entries = summary.attachments || [];

    /* Older payloads have carried the photos only on the reviews themselves. */
    if (!entries.length) {
      entries = ((data.productReviews && data.productReviews.reviews) || []).reduce(function (all, review) {
        (review.attachments || []).forEach(function (a) {
          all.push({ reviewId: review.id, attachment: a.attachment || a });
        });
        return all;
      }, []);
    }

    var seen = {};
    var out = [];
    entries.forEach(function (entry) {
      var attachment = entry && (entry.attachment || entry);
      var id = attachment && attachment.id;
      if (!attachment || (id && seen[id])) return;
      if (id) seen[id] = true;
      var url = pickImage(attachment);
      if (url) out.push({ url: url, reviewId: entry.reviewId });
    });
    return out;
  }

  function boot() {
    var root = document.querySelector('[data-fi-tpc]');
    var widget = document.querySelector('[data-fi-trustpilot-product]');
    if (!root || !widget) return;

    var configNode = root.querySelector('[data-fi-tpc-config]');
    var config = {};
    try {
      config = configNode ? JSON.parse(configNode.textContent) : {};
    } catch (e) {
      config = {};
    }

    var narrow = window.matchMedia('(max-width: ' + (config.breakpoint || 749) + 'px)');
    var track = root.querySelector('[data-fi-tpc-track]');
    var viewport = root.querySelector('[data-fi-tpc-viewport]');
    var dots = root.querySelector('[data-fi-tpc-dots]');
    var prev = root.querySelector('[data-fi-tpc-prev]');
    var next = root.querySelector('[data-fi-tpc-next]');
    if (!track || !viewport) return;

    var state = { loaded: false, loading: false, gaveUp: false, sku: null };

    /* ---- handing control back ----
       The iframe widget was taken out of the bootstrap's reach so a phone would
       not pay to build a 700px list it is not going to show. If this carousel
       cannot deliver, that has to be undone completely, including asking
       Trustpilot to draw the element it skipped. */
    /* known === true means the fetch succeeded and the range genuinely has no
       reviews, so the section can swap in the store-wide widget straight away.
       Every other way of giving up — a failed fetch, a blocked endpoint — leaves
       that unknown, and the section goes back to watching the iframe. */
    function restoreWidget(known) {
      if (state.gaveUp) return;
      state.gaveUp = true;
      root.hidden = true;
      widget.hidden = false;
      widget.classList.add('trustpilot-widget');
      if (window.Trustpilot && window.Trustpilot.loadFromElement) {
        window.Trustpilot.loadFromElement(widget, true);
      }
      /* The section owns the no-reviews fallback; it stood down while this was in
         charge, so tell it to take over again. */
      if (!window.FITP) return;
      if (known === true && typeof window.FITP.showTrustpilotFallback === 'function') {
        window.FITP.showTrustpilotFallback();
      } else if (typeof window.FITP.startFallbackPoll === 'function') {
        window.FITP.startFallbackPoll();
      }
    }

    function claimWidget() {
      widget.hidden = true;
      widget.classList.remove('trustpilot-widget');
    }

    /* ---- summary: the range's overall score ---- */
    function renderSummary(data) {
      var host = root.querySelector('[data-fi-tpc-summary]');
      if (!host) return;
      host.textContent = '';

      var summary = data.productReviewsSummary || {};
      var average = Number(summary.starsAverage || 0);
      var total = (summary.numberOfReviews && summary.numberOfReviews.total) || 0;

      host.appendChild(stars(average, 22));

      var line = el('div', 'fi-tpc__score');
      line.appendChild(el('strong', 'fi-tpc__avg', average.toFixed(1)));
      line.appendChild(
        el(
          'span',
          'fi-tpc__count',
          (config.summaryText || 'from [count] reviews of this range').replace('[count]', formatCount(total, config.locale))
        )
      );
      host.appendChild(line);

      /* Quality / Value for money and friends — Trustpilot scores these per
         product, so they belong with the range summary rather than each card. */
      var attrs = (data.product && data.product.attributeRatings) || [];
      if (attrs.length) {
        var chips = el('div', 'fi-tpc__attrs');
        attrs.forEach(function (attr) {
          var chip = el('span', 'fi-tpc__attr');
          chip.appendChild(el('span', 'fi-tpc__attr-name', attr.name));
          chip.appendChild(el('span', 'fi-tpc__attr-value', Number(attr.rating).toFixed(1)));
          chips.appendChild(chip);
        });
        host.appendChild(chips);
      }
    }

    /* ---- the photo strip, above the carousel ---- */
    function renderGallery(data) {
      var host = root.querySelector('[data-fi-tpc-gallery]');
      if (!host) return;
      host.textContent = '';
      host.hidden = true;
      if (config.showImages === false) return;

      var images = galleryImages(data);
      if (!images.length) return;

      images.slice(0, config.maxImages || 8).forEach(function (image) {
        /* A button, not a bare img: it jumps the carousel to the matching review,
           so it has to be reachable by keyboard and announce itself as an action. */
        var cell = el('button', 'fi-tpc__thumb');
        cell.type = 'button';
        if (image.reviewId) cell.dataset.reviewId = image.reviewId;
        cell.setAttribute('aria-label', config.photoText || 'See the review this photo came from');

        var img = el('img');
        img.src = image.url;
        img.loading = 'lazy';
        img.decoding = 'async';
        /* Customer photos carry no caption anywhere in the payload, and the review
           the thumbnail leads to says what it shows. */
        img.alt = '';
        cell.appendChild(img);
        host.appendChild(cell);
      });

      host.hidden = false;
    }

    function scrollToReview(id) {
      var cards = Array.prototype.slice.call(track.children);
      for (var i = 0; i < cards.length; i++) {
        if (cards[i].dataset.reviewId === id) {
          viewport.scrollTo({ left: i * cardStep(), behavior: 'smooth' });
          return;
        }
      }
    }

    /* ---- one card per review ---- */
    function renderCard(review) {
      var card = el('li', 'fi-tpc__card');
      if (review.id) card.dataset.reviewId = review.id;

      var head = el('div', 'fi-tpc__cardhead');
      head.appendChild(stars(review.stars, 18));
      var badge = el('span', 'fi-tpc__verified', config.verifiedText || 'Verified');
      head.appendChild(badge);
      card.appendChild(head);

      if (review.content) card.appendChild(el('p', 'fi-tpc__text', review.content));

      /* No photos in here on purpose — they all live in the strip above, so a card
         is text only and every card is the same height. */

      var meta = el('div', 'fi-tpc__meta');
      var who = (review.consumer && review.consumer.displayName) || '';
      if (who) meta.appendChild(el('span', 'fi-tpc__who', who));
      var when = formatDate(review.createdAt, config.locale);
      if (when) meta.appendChild(el('span', 'fi-tpc__when', when));
      card.appendChild(meta);

      return card;
    }

    function renderDots(count) {
      if (!dots) return;
      dots.textContent = '';
      if (count < 2) return;
      for (var i = 0; i < count; i++) {
        var dot = el('button', 'fi-tpc__dot');
        dot.type = 'button';
        dot.setAttribute('aria-label', (config.goToText || 'Go to review [n]').replace('[n]', i + 1));
        dot.dataset.index = String(i);
        dots.appendChild(dot);
      }
    }

    function cardStep() {
      var first = track.firstElementChild;
      if (!first) return viewport.clientWidth;
      var styles = window.getComputedStyle(track);
      var gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return first.getBoundingClientRect().width + gap;
    }

    function activeIndex() {
      var step = cardStep();
      return step ? Math.round(viewport.scrollLeft / step) : 0;
    }

    var navQueued = false;
    function updateNav() {
      if (navQueued) return;
      navQueued = true;
      window.requestAnimationFrame(function () {
        navQueued = false;
        var index = activeIndex();
        if (dots) {
          Array.prototype.forEach.call(dots.children, function (dot, i) {
            dot.classList.toggle('is-on', i === index);
            dot.setAttribute('aria-current', i === index ? 'true' : 'false');
          });
        }
        /* 2px of slack: sub-pixel scroll positions otherwise leave the last
           arrow enabled at the very end of the track. */
        var atStart = viewport.scrollLeft <= 2;
        var atEnd = viewport.scrollLeft + viewport.clientWidth >= viewport.scrollWidth - 2;
        if (prev) prev.disabled = atStart;
        if (next) next.disabled = atEnd;
      });
    }

    function scrollByCards(direction) {
      viewport.scrollBy({ left: direction * cardStep(), behavior: 'smooth' });
    }

    if (prev) prev.addEventListener('click', function () { scrollByCards(-1); });
    if (next) next.addEventListener('click', function () { scrollByCards(1); });
    viewport.addEventListener('scroll', updateNav, { passive: true });
    window.addEventListener('resize', updateNav);

    if (dots) {
      dots.addEventListener('click', function (event) {
        var dot = event.target.closest('[data-index]');
        if (!dot) return;
        viewport.scrollTo({ left: Number(dot.dataset.index) * cardStep(), behavior: 'smooth' });
      });
    }

    var gallery = root.querySelector('[data-fi-tpc-gallery]');
    if (gallery) {
      gallery.addEventListener('click', function (event) {
        var thumb = event.target.closest('[data-review-id]');
        if (thumb) scrollToReview(thumb.dataset.reviewId);
      });
    }

    function render(data) {
      var reviews = (data.productReviews && data.productReviews.reviews) || [];
      if (!reviews.length) {
        /* Answered, and the answer is none — the section can swap immediately
           rather than spending eight seconds watching an iframe that will never
           fill. */
        restoreWidget(true);
        return;
      }

      renderSummary(data);
      renderGallery(data);

      track.textContent = '';
      reviews.forEach(function (review) {
        track.appendChild(renderCard(review));
      });
      renderDots(reviews.length);

      var link = root.querySelector('[data-fi-tpc-all]');
      var profile = data.links && data.links.profileUrl;
      if (link && profile) link.href = profile;
      else if (link) link.hidden = true;

      root.hidden = false;
      state.loaded = true;
      updateNav();
    }

    function load() {
      if (state.loading || state.gaveUp) return;

      var sku = widget.getAttribute('data-sku');
      if (!sku) {
        restoreWidget();
        return;
      }
      /* The variant picker rewrites data-sku on single-product (non-range)
         products, so a repeat call with the same list is just noise. */
      if (state.loaded && sku === state.sku) return;
      state.sku = sku;
      state.loading = true;

      var template = widget.getAttribute('data-template-id');
      var params = [
        'businessUnitId=' + encodeURIComponent(widget.getAttribute('data-businessunit-id') || ''),
        'locale=' + encodeURIComponent(widget.getAttribute('data-locale') || 'en-GB'),
        'sku=' + encodeURIComponent(sku),
        'reviewsPerPage=' + encodeURIComponent(config.reviewsPerPage || 10)
      ];
      var languages = widget.getAttribute('data-review-languages');
      if (languages) params.push('reviewLanguages=' + encodeURIComponent(languages));

      fetch(ENDPOINT + encodeURIComponent(template) + '?' + params.join('&'), {
        headers: { Accept: 'application/json' },
        credentials: 'omit'
      })
        .then(function (response) {
          if (!response.ok) throw new Error('status ' + response.status);
          return response.json();
        })
        .then(function (data) {
          state.loading = false;
          render(data);
        })
        .catch(function () {
          state.loading = false;
          /* Endpoint moved, blocked or offline — the iframe is still a working
             way to show these reviews, so hand back rather than show nothing. */
          restoreWidget();
        });
    }

    /* ---- when to fetch ----
       Reviews sit well below the fold, so nothing is requested until the section
       is close to being scrolled into view. Without IntersectionObserver the
       request simply happens up front. */
    function watch() {
      if (!('IntersectionObserver' in window)) {
        load();
        return;
      }
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            observer.disconnect();
            load();
          });
        },
        { rootMargin: '300px 0px' }
      );
      observer.observe(root.parentNode || root);
    }

    /* A variant change on a non-range product swaps the SKU, and with it which
       reviews apply. */
    if ('MutationObserver' in window) {
      new MutationObserver(function () {
        if (!narrow.matches || state.gaveUp) return;
        load();
      }).observe(widget, { attributes: true, attributeFilter: ['data-sku'] });
    }

    if (narrow.matches) {
      claimWidget();
      watch();
    }

    /* Rotating a phone into landscape crosses the breakpoint on a page that is
       already rendered. Once this carousel has its data it stays the phone
       layout; the CSS is what hides it on a wide screen. */
    function onBreakpoint() {
      if (state.gaveUp) return;
      if (narrow.matches) {
        claimWidget();
        if (!state.loaded) watch();
      } else if (!state.loaded) {
        /* Never rendered and no longer needed — give the iframe back so a wide
           screen still gets its list. */
        restoreWidget();
      }
    }
    if (typeof narrow.addEventListener === 'function') narrow.addEventListener('change', onBreakpoint);
    else if (typeof narrow.addListener === 'function') narrow.addListener(onBreakpoint);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
